import { useState } from "react";
import { useForm } from "react-hook-form";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  FormFeedback,
  Alert,
  Spinner,
} from "reactstrap";
import { toast } from "react-toastify";
import newApi from "service/newApi";

const WebhookSigningKeyModal = ({ isOpen, toggle, connector, onSave }) => {
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();

  const hasWebhookKey = connector?.config?.has_webhook_key;

  const onSubmit = async (data) => {
    if (!connector?.id) {
      toast.error("Conector não encontrado!");
      return;
    }

    setLoading(true);
    try {
      const response = await newApi.patch(
        `/zendesk-connectors/${connector.id}/webhook-signing-key`,
        {
          webhook_signing_key: data.webhook_signing_key,
        },
      );

      toast.success(
        `Chave de webhook ${hasWebhookKey ? "atualizada" : "cadastrada"} com sucesso!`,
      );
      reset();
      onSave();
    } catch (error) {
      const message =
        error.response?.data?.message ||
        `Erro ao ${hasWebhookKey ? "atualizar" : "cadastrar"} chave de webhook`;
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="md">
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalHeader toggle={handleClose}>
          <i className="fas fa-key me-2" />
          {hasWebhookKey ? "Atualizar" : "Cadastrar"} Chave de Webhook
        </ModalHeader>

        <ModalBody>
          <Alert color="info" className="mb-4">
            <div className="d-flex">
              <i className="fas fa-info-circle me-2 mt-1" />
              <div>
                <h6 className="mb-2">Sobre a Chave de Webhook</h6>
                <p className="mb-1 small">
                  A chave de assinatura do webhook é fornecida pelo Zendesk ao
                  configurar um webhook no seu painel administrativo. Esta chave
                  garante a autenticidade das requisições enviadas pelo Zendesk.
                </p>
                {!hasWebhookKey && (
                  <p className="mb-0 small text-warning">
                    <strong>Nota:</strong> Seu conector ainda não possui uma
                    chave de webhook configurada. Adicione-a para garantir a
                    segurança das integrações.
                  </p>
                )}
              </div>
            </div>
          </Alert>

          <FormGroup>
            <Label for="webhook_signing_key">
              Chave de Assinatura do Webhook{" "}
              <span className="text-danger">*</span>
            </Label>
            <input
              className={`form-control-alternative custom-input ${
                errors.webhook_signing_key ? "is-invalid" : ""
              }`}
              type="password"
              id="webhook_signing_key"
              placeholder="Cole aqui a chave de assinatura do webhook"
              autoComplete="new-password"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              data-lpignore="true"
              data-form-type="other"
              {...register("webhook_signing_key", {
                required: "Chave de assinatura do webhook é obrigatória",
                minLength: {
                  value: 10,
                  message: "A chave deve ter pelo menos 10 caracteres",
                },
              })}
            />
            {errors.webhook_signing_key && (
              <FormFeedback>{errors.webhook_signing_key?.message}</FormFeedback>
            )}
            <small className="form-text text-muted">
              Copie esta chave do painel administrativo do Zendesk
            </small>
          </FormGroup>

          {hasWebhookKey && (
            <Alert color="warning" className="mt-3">
              <i className="fas fa-exclamation-triangle me-2" />
              <strong>Atenção:</strong> Ao atualizar a chave, a anterior será
              substituída. Certifique-se de que a nova chave está correta.
            </Alert>
          )}
        </ModalBody>

        <ModalFooter>
          <Button color="secondary" onClick={handleClose} disabled={loading}>
            Cancelar
          </Button>
          <Button color="primary" type="submit" disabled={loading}>
            {loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                {hasWebhookKey ? "Atualizando..." : "Cadastrando..."}
              </>
            ) : (
              <>
                <i className="fas fa-key me-2" />
                {hasWebhookKey ? "Atualizar" : "Cadastrar"} Chave
              </>
            )}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default WebhookSigningKeyModal;
